import dayjs from "dayjs";

import type {
  ChannelData,
  DailyMetric,
  FixedKpiMetric,
  Granularity,
  KpiMetric,
  KpisPerChannelResponse,
} from "@tracify/shared";
import { METRICS_LABELS } from "@/constants/constants";
import { alignComparisonDataByIndex } from "@lib/util-functions/comparisonBuckets";

export type ChannelOverviewChartMetric =
  | "customer_revenue"
  | "customer_purchase"
  | "total_spend"
  | "return_on_ad_spend"
  | "average_order_value"
  | "cost_per_order"
  | "customer_acquisition_cost"
  | "conversion_rate"
  | "new_customer_purchase_ratio"
  | "new_visitor_pageview_ratio"
  | "customer_unique_addtocart"
  | "customer_unique_productview"
  | "customer_unique_pageview"
  | "customer_addtocart"
  | "customer_productview"
  | "customer_pageview";

export type ChannelOverviewSegment = "new" | "returning" | "total";

export type MarketingChannelOverviewChartPoint = {
  date: string;
} & Record<string, string | number>;

const Y_AXIS_MIN_WIDTH = 40;
const Y_AXIS_MAX_WIDTH = 100;
const Y_AXIS_CHARACTER_WIDTH = 7;
const Y_AXIS_HORIZONTAL_PADDING = 20;

const CHANNEL_OVERVIEW_CHART_METRIC_VALUES = [
  "customer_revenue",
  "customer_purchase",
  "total_spend",
  "return_on_ad_spend",
  "average_order_value",
  "cost_per_order",
  "customer_acquisition_cost",
  "conversion_rate",
  "new_customer_purchase_ratio",
  "new_visitor_pageview_ratio",
  "customer_unique_addtocart",
  "customer_unique_productview",
  "customer_unique_pageview",
  "customer_addtocart",
  "customer_productview",
  "customer_pageview",
] as const satisfies readonly ChannelOverviewChartMetric[];

export const CHANNEL_OVERVIEW_CHART_METRICS: Array<{
  value: ChannelOverviewChartMetric;
  label: string;
}> = CHANNEL_OVERVIEW_CHART_METRIC_VALUES.map((value) => ({
  value,
  label: METRICS_LABELS[value],
}));

const INTEGER_Y_AXIS_METRICS = new Set<ChannelOverviewChartMetric>([
  "customer_purchase",
  "customer_unique_addtocart",
  "customer_unique_productview",
  "customer_unique_pageview",
  "customer_addtocart",
  "customer_productview",
  "customer_pageview",
]);

const FIXED_METRIC_KEYS = new Set<keyof FixedKpiMetric>([
  "ad_spend",
  "influencer_fixed_commission",
  "clicks",
  "link_click",
  "important_clicks",
  "impressions",
  "reach",
  "comment",
  "landing_page_view",
  "conversions",
  "all_conversions",
  "video_view",
  "onsite_conversion_post_save",
  "video_avg_time_watched_actions",
  "click_through_rate",
  "frequency",
  "thumbstop_ratio",
  "cost_per_click",
  "cost_per_mille",
]);

const sortByDate = (metrics: DailyMetric[]): DailyMetric[] =>
  [...metrics].sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

/**
 * Helper function to get the correct data array based on granularity.
 */
export const getGranularityChannelData = (
  channelData: ChannelData,
  granularity: Granularity
): DailyMetric[] => {
  switch (granularity) {
    case "weekly":
      return channelData.weekly && channelData.weekly.length > 0
        ? channelData.weekly
        : (channelData.daily ?? []);
    case "monthly":
      return channelData.monthly && channelData.monthly.length > 0
        ? channelData.monthly
        : (channelData.daily ?? []);
    case "daily":
    default:
      return channelData.daily ?? [];
  }
};

export const getSortedGranularityChannelData = (
  channelData: ChannelData,
  granularity: Granularity
): DailyMetric[] =>
  sortByDate(getGranularityChannelData(channelData, granularity));

export const getComparisonMetricKey = (channel: string): string =>
  `compared_${channel}`;

export const getMarketingChannelOverviewYAxisWidth = ({
  chartData,
  formatAxisValue,
}: {
  chartData: MarketingChannelOverviewChartPoint[];
  formatAxisValue: (value: number) => string;
}): number => {
  let longestLabelLength = formatAxisValue(0).length;

  chartData.forEach((point) => {
    Object.values(point).forEach((value) => {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return;
      }

      longestLabelLength = Math.max(
        longestLabelLength,
        formatAxisValue(Math.abs(value)).length
      );
    });
  });

  const estimatedWidth =
    longestLabelLength * Y_AXIS_CHARACTER_WIDTH + Y_AXIS_HORIZONTAL_PADDING;

  return Math.max(Y_AXIS_MIN_WIDTH, Math.min(estimatedWidth, Y_AXIS_MAX_WIDTH));
};

export const shouldUseIntegerYAxisTicks = (
  metric: ChannelOverviewChartMetric
): boolean => INTEGER_Y_AXIS_METRICS.has(metric);

export const getDailyMetricValue = ({
  metric,
  point,
  segment,
}: {
  metric: ChannelOverviewChartMetric | keyof FixedKpiMetric;
  point: DailyMetric;
  segment: ChannelOverviewSegment;
}): number => {
  if (FIXED_METRIC_KEYS.has(metric as keyof FixedKpiMetric)) {
    return point.fixed[metric as keyof FixedKpiMetric] ?? 0;
  }

  return point[segment][metric as keyof KpiMetric] ?? 0;
};

export const buildMarketingChannelOverviewChartData = ({
  data,
  comparedData,
  channels,
  metric,
  segment,
  granularity,
}: {
  data?: KpisPerChannelResponse;
  comparedData?: KpisPerChannelResponse;
  channels: string[];
  metric: ChannelOverviewChartMetric;
  segment: ChannelOverviewSegment;
  granularity: Granularity;
}): MarketingChannelOverviewChartPoint[] => {
  if (!data || channels.length === 0) return [];

  const dates = new Set<string>();
  const valuesByChannel = new Map<string, Map<string, number>>();
  const comparisonValuesByChannel = new Map<string, Map<string, number>>();

  channels.forEach((channel) => {
    const channelData = data.channels[channel];
    if (!channelData) return;

    const currentSeries = getSortedGranularityChannelData(
      channelData,
      granularity
    );
    const currentValues = new Map<string, number>();

    currentSeries.forEach((point) => {
      dates.add(point.date);
      currentValues.set(
        point.date,
        getDailyMetricValue({ metric, point, segment })
      );
    });

    valuesByChannel.set(channel, currentValues);

    const comparedChannelData = comparedData?.channels[channel];
    if (!comparedChannelData) return;

    const comparedSeries = getSortedGranularityChannelData(
      comparedChannelData,
      granularity
    );
    const comparisonMap = alignComparisonDataByIndex(
      currentSeries,
      comparedSeries
    );
    const comparisonValues = new Map<string, number>();

    currentSeries.forEach((point) => {
      const comparedPoint = comparisonMap.get(point.date);
      comparisonValues.set(
        point.date,
        comparedPoint
          ? getDailyMetricValue({ metric, point: comparedPoint, segment })
          : 0
      );
    });

    comparisonValuesByChannel.set(channel, comparisonValues);
  });

  return [...dates]
    .sort((a, b) => dayjs(a).diff(dayjs(b)))
    .map((date) => {
      const chartPoint: MarketingChannelOverviewChartPoint = { date };

      channels.forEach((channel) => {
        chartPoint[channel] = valuesByChannel.get(channel)?.get(date) ?? 0;

        const comparisonValues = comparisonValuesByChannel.get(channel);
        if (comparisonValues) {
          chartPoint[getComparisonMetricKey(channel)] =
            comparisonValues.get(date) ?? 0;
        }
      });

      return chartPoint;
    });
};
